import { CODE_PUBLIC } from "@anysola/contracts/web/launch"
import type { SiteInfo, SitePulse } from "@anysola/contracts/web/pages"
import { ClerkProvider } from "@clerk/react"
import {
  createRootRoute,
  HeadContent,
  Outlet,
  Scripts,
  useRouterState,
  type ErrorComponentProps,
} from "@tanstack/react-router"
import type { ReactNode } from "react"

import {
  GLOBAL_ERROR_BODY,
  GlobalErrorBody,
} from "@/components/feedback/global-error"
import { PageNotFound } from "@/components/feedback/page-not-found"
import { JsonLd, organizationJsonLd } from "@/components/seo/json-ld"
import { clerkAppearance } from "@anysola/auth/react"
import { applySite } from "@/lib/site"
import { cn } from "@anysola/ui/utils"
import { getPulse, getSite } from "@/server/web"
import globalsCss from "@/styles/globals.css?url"

/**
 * The document every page renders in (the Next app's `RootDocument` and
 * `app/(site)/layout.tsx`): dark only, the skip link, Clerk (signed-in state
 * is read in the browser; nothing on these pages needs it on the server),
 * and who is behind the site (schema.org Organization) on every page.
 */
export const Route = createRootRoute({
  // The status line rides along so the server render already has the
  // footer badge and the incident notice; `usePulse` refreshes it after.
  loader: async (): Promise<{ site: SiteInfo; pulse: SitePulse | null }> => {
    const [site, pulse] = await Promise.all([
      getSite(),
      getPulse().catch(() => null),
    ])
    applySite(site)
    return { site, pulse }
  },
  // Runtime config: read once, kept for the session.
  staleTime: Infinity,
  shouldReload: false,
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
    ],
    links: [
      { rel: "stylesheet", href: globalsCss },
      { rel: "manifest", href: "/manifest.webmanifest" },
      {
        rel: "icon",
        href: "/favicon.ico",
        type: "image/x-icon",
        sizes: "16x16",
      },
      { rel: "icon", href: "/icon.png", type: "image/png", sizes: "192x192" },
      {
        rel: "apple-touch-icon",
        href: "/apple-icon.png",
        type: "image/png",
        sizes: "180x180",
      },
    ],
  }),
  component: RootComponent,
  errorComponent: RootErrorDocument,
  notFoundComponent: PageNotFound,
})

/** Clerk around the pages, with the runtime key. */
function Providers({
  site,
  children,
}: {
  site: SiteInfo
  children: ReactNode
}) {
  return (
    <ClerkProvider
      publishableKey={site.clerkPublishableKey}
      signInUrl={site.clerkSignInUrl || undefined}
      signUpUrl={site.clerkSignUpUrl || undefined}
      appearance={clerkAppearance}
    >
      {children}
    </ClerkProvider>
  )
}

function RootComponent() {
  const { site } = Route.useLoaderData()
  applySite(site)
  // No Organization block on the unknown-URL 404 (`global-not-found.tsx`,
  // and /code until launch) or a missing news post (Next served its `not-found.tsx` without the
  // layout's body); a missing help article kept it.
  const notFound = useRouterState({
    select: (state) =>
      state.matches.some(
        (match) =>
          match.routeId === "/$" ||
          (match.routeId === "/code" && !CODE_PUBLIC) ||
          (match.status === "notFound" && match.routeId === "/news/$slug")
      ),
  })
  return (
    <RootDocument>
      <Providers site={site}>
        {!notFound && <JsonLd data={organizationJsonLd()} />}
        <Outlet />
      </Providers>
    </RootDocument>
  )
}

/** `app/global-error.tsx`: the document itself failed. */
function RootErrorDocument(props: ErrorComponentProps) {
  return (
    <RootDocument bodyClassName={GLOBAL_ERROR_BODY} skipLink={false}>
      <GlobalErrorBody {...props} />
    </RootDocument>
  )
}

function RootDocument({
  children,
  bodyClassName,
  skipLink = true,
}: {
  children: ReactNode
  bodyClassName?: string
  skipLink?: boolean
}) {
  return (
    <html lang="en" suppressHydrationWarning className={cn("dark antialiased")}>
      <head>
        <HeadContent />
      </head>
      {/* Browser extensions (ColorZilla's `cz-shortcut-listen`, Grammarly's
          `data-gr-*`) stamp attributes onto <body> before React hydrates.
          This only silences attribute differences on this one element —
          mismatches anywhere inside it are still reported. */}
      <body suppressHydrationWarning className={bodyClassName}>
        {skipLink && (
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:shadow-lg"
          >
            Skip to content
          </a>
        )}
        {/* The link's target is the shell's own <main> (SiteShell), so it
            really does skip the navigation. */}
        {children}
        <Scripts />
      </body>
    </html>
  )
}
